// Stenando — tabbed product showcase.
// Each panel is rendered by a tab block and carries its own data-label, count,
// title and url. This builds the tab bar from those panels, so labels always
// match the blocks, then toggles panel visibility on click.
class StenandoProducts extends HTMLElement {
  connectedCallback() {
    this.tabBar = this.querySelector('.stenando-products__tabs');
    this.panelWrap = this.querySelector('.stenando-products__panels');
    this.panels = Array.from(this.querySelectorAll('.stenando-products__panel'));
    this.count = this.querySelector('.stenando-products__count');
    this.shopLink = this.querySelector('.stenando-products__link');
    this.shopLabel = this.shopLink?.querySelector('[data-label-slot]');

    if (!this.panels.length) return;

    // One panel needs no tab bar, but still syncs the heading.
    if (this.panels.length > 1 && this.tabBar) {
      this.buildTabs();
      this.tabs = Array.from(this.tabBar.querySelectorAll('.stenando-products__tab'));
      this.tabBar.hidden = false;

      this.tabBar.addEventListener('click', (event) => {
        const tab = event.target.closest('.stenando-products__tab');
        if (tab) this.activate(this.tabs.indexOf(tab), { focus: false });
      });

      this.tabBar.addEventListener('keydown', (event) => this.handleKeydown(event));
    }

    // Mark the active panel before switching the CSS to enhanced mode, so no
    // frame renders with every panel hidden.
    this.activate(0, { focus: false });
    if (this.panelWrap) this.panelWrap.setAttribute('data-enhanced', '');
  }

  buildTabs() {
    const prefix = this.closest('[id]')?.id ?? 'stenando-products';
    this.tabBar.innerHTML = '';

    this.panels.forEach((panel, index) => {
      const tabId = `${prefix}-tab-${index}`;
      const panelId = `${prefix}-panel-${index}`;

      panel.id = panelId;
      panel.setAttribute('aria-labelledby', tabId);

      const button = document.createElement('button');
      button.type = 'button';
      button.id = tabId;
      button.className = 'stenando-products__tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panelId);
      button.textContent = panel.dataset.label || 'Category';

      this.tabBar.appendChild(button);
    });
  }

  handleKeydown(event) {
    if (!event.target.closest('.stenando-products__tab')) return;

    const last = this.tabs.length - 1;
    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let next = null;

    if (event.key === 'ArrowRight') next = current === last ? 0 : current + 1;
    if (event.key === 'ArrowLeft') next = current === 0 ? last : current - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;

    if (next === null) return;

    event.preventDefault();
    this.activate(next, { focus: true });
  }

  activate(index, { focus } = { focus: false }) {
    this.tabs?.forEach((tab, position) => {
      const active = position === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel, position) => {
      panel.classList.toggle('is-active', position === index);
    });

    this.syncHeading(this.panels[index]);

    if (focus) this.tabs?.[index]?.focus();
  }

  syncHeading(panel) {
    if (!panel) return;

    const { count, title, url } = panel.dataset;

    if (this.count && count != null && title) {
      this.count.textContent = (this.count.dataset.template || '%count% in %title%')
        .replace('%count%', count)
        .replace('%title%', title);
    }

    if (this.shopLink && url) this.shopLink.href = url;

    if (this.shopLabel && title) {
      this.shopLabel.textContent = (this.shopLink?.dataset.template || 'Shop %title%').replace('%title%', title);
    }
  }
}

if (!customElements.get('stenando-products-component')) {
  customElements.define('stenando-products-component', StenandoProducts);
}
